import { memo, useMemo } from 'react';
import type { FormTabData, FormTabFieldData } from '@/services/api-types';

export interface TourStopFormTabContentProps {
  tab: FormTabData;
}

interface FieldGroup {
  sectionId: string;
  sectionTitle: string;
  fields: FormTabFieldData[];
}

/**
 * Read-only label/value rendering for one form tab on the live tour stop
 * detail page. Mirrors the section-by-section grouping of the Records Search
 * completion summary, but uses CSS hooks scoped to the tour stop area (`stop-form-tab-*`)
 * so it sits cleanly alongside the Tasks / Comments / Attachments tabs.
 *
 * Empty values render as an em-dash placeholder so each row stays the same
 * height — the surface should never look broken just because a particular
 * field hasn't been filled in yet.
 */
export const TourStopFormTabContent = memo(function TourStopFormTabContent({
  tab,
}: TourStopFormTabContentProps) {
  const groups = useMemo<FieldGroup[]>(() => {
    const out: FieldGroup[] = [];
    const indexById = new Map<string, number>();
    for (const field of tab.fields ?? []) {
      const sectionId = field.sectionId ?? '';
      const sectionTitle = field.sectionTitle ?? '';
      const key = `${sectionId}::${sectionTitle}`;
      let idx = indexById.get(key);
      if (idx == null) {
        idx = out.length;
        indexById.set(key, idx);
        out.push({ sectionId, sectionTitle, fields: [] });
      }
      out[idx].fields.push(field);
    }
    return out;
  }, [tab.fields]);

  if (groups.length === 0) {
    return (
      <div className="stop-form-tab-empty">
        No fields have been selected to show on this tab yet.
      </div>
    );
  }

  return (
    <div className="stop-form-tab">
      {groups.map((group, gi) => (
        <div key={`${group.sectionId}-${gi}`} className="stop-form-tab-section">
          {group.sectionTitle ? (
            <div className="stop-form-tab-section-title">{group.sectionTitle}</div>
          ) : null}
          <div className="stop-form-tab-grid">
            {group.fields.map((field) => (
              <div key={field.key} className="stop-form-tab-row">
                <div className="stop-form-tab-label">{field.label}</div>
                <div className="stop-form-tab-value">
                  {field.displayValue && field.displayValue.length > 0
                    ? field.displayValue
                    : '—'}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
});
